/**
 * Resource catalog.
 *
 *  - Phase 1 shipped just iron-ore + iron-ingot (the MVP loop).
 *  - Phase 5 added coal + refined-fuel for the Combustion Cell chain.
 *  - Phase 6 expands to a real T1–T2 Materials track plus T1 Energy:
 *      Materials: copper-ore → copper-plate → copper-wire → basic-circuit;
 *                 bauxite → alumina (T2 light-metal stock).
 *      Energy:    water (T1 fluid feedstock), hydrogen (T1 gas),
 *                 oxygen (T1 gas), liquid-hydrogen (T1 cryogenic fluid).
 *
 * `phase` drives drone-class compatibility (freighter = solid, tanker = fluid,
 * runner = gas, cryo = cryogenic fluid). `cryogenic: true` resources can only
 * ride the cryo class — and they bleed cargo every tick they're in transit
 * (see `DroneSpec.boilOffPerTick`). Most resources don't need the flag, so
 * it's optional and defaults to false.
 */
export const RESOURCES = {
    // ── T1 raws (solids) ────────────────────────────────────────────────────
    'iron-ore': {
        id: 'iron-ore',
        name: 'Iron Ore',
        tier: 1,
        phase: 'solid',
        basePrice: 1,
    },
    coal: {
        id: 'coal',
        name: 'Coal',
        tier: 1,
        phase: 'solid',
        basePrice: 1,
    },
    'copper-ore': {
        id: 'copper-ore',
        name: 'Copper Ore',
        tier: 1,
        phase: 'solid',
        basePrice: 2,
    },
    bauxite: {
        id: 'bauxite',
        name: 'Bauxite',
        tier: 1,
        phase: 'solid',
        basePrice: 2,
    },
    // ── T1 fluid feedstock ───────────────────────────────────────────────────
    water: {
        // Atmospheric Collector pulls water from the air; the Hydrolysis Cell
        // splits it into hydrogen + oxygen. Cheapest fluid in the catalog so the
        // Energy track has a free-ish on-ramp.
        id: 'water',
        name: 'Water',
        tier: 1,
        phase: 'fluid',
        basePrice: 0.5,
    },
    // ── T1 gases ─────────────────────────────────────────────────────────────
    hydrogen: {
        id: 'hydrogen',
        name: 'Hydrogen',
        tier: 1,
        phase: 'gas',
        basePrice: 4,
    },
    oxygen: {
        id: 'oxygen',
        name: 'Oxygen',
        tier: 1,
        phase: 'gas',
        basePrice: 2,
    },
    // ── T1 cryogenics ────────────────────────────────────────────────────────
    'liquid-hydrogen': {
        // Compressed + liquefied via the Cryo Separator. The lore-honest reason
        // cryo drones exist: only they can carry this without losing it all
        // before arrival. Worth a lot per unit because of that handling cost.
        id: 'liquid-hydrogen',
        name: 'Liquid Hydrogen',
        tier: 1,
        phase: 'fluid',
        basePrice: 12,
        cryogenic: true,
    },
    // ── T2 refined ────────────────────────────────────────────────────────────
    'iron-ingot': {
        id: 'iron-ingot',
        name: 'Iron Ingot',
        tier: 2,
        phase: 'solid',
        basePrice: 5,
    },
    'copper-plate': {
        id: 'copper-plate',
        name: 'Copper Plate',
        tier: 2,
        phase: 'solid',
        basePrice: 8,
    },
    alumina: {
        id: 'alumina',
        name: 'Alumina',
        tier: 2,
        phase: 'solid',
        basePrice: 7,
    },
    'refined-fuel': {
        id: 'refined-fuel',
        name: 'Refined Fuel',
        tier: 2,
        phase: 'fluid',
        basePrice: 8,
    },
    // ── T2 fabricated ────────────────────────────────────────────────────────
    'copper-wire': {
        id: 'copper-wire',
        name: 'Copper Wire',
        tier: 2,
        phase: 'solid',
        basePrice: 12,
    },
    'basic-circuit': {
        id: 'basic-circuit',
        name: 'Basic Circuit',
        tier: 2,
        phase: 'solid',
        basePrice: 25,
    },
    // ── P6A Biotech track ────────────────────────────────────────────────────
    // Biotech is the lore's third resource track (alongside Materials + Energy).
    // Algae Reactor pulls slurry from a water-source tile; the Fermentor turns
    // cultured slurry + biomass into bio-fuel; the Culture Lab brews pharma
    // precursors. T1 raws are fluid; the T2 pharma precursor is cryogenic so
    // the new Cryo class drone has a non-LH₂ reason to exist on day one.
    'algae-slurry': {
        id: 'algae-slurry',
        name: 'Algae Slurry',
        tier: 1,
        phase: 'fluid',
        basePrice: 1.5,
    },
    'microbial-culture': {
        id: 'microbial-culture',
        name: 'Microbial Culture',
        tier: 1,
        phase: 'fluid',
        basePrice: 6,
    },
    'bio-mass': {
        // Solid, not fluid — the dried biomass cake the Fermentor pulps into fuel.
        // Solid keeps the chain compatible with freighters early on.
        id: 'bio-mass',
        name: 'Bio-Mass',
        tier: 1,
        phase: 'solid',
        basePrice: 4,
    },
    'fermented-fuel': {
        id: 'fermented-fuel',
        name: 'Fermented Fuel',
        tier: 2,
        phase: 'fluid',
        basePrice: 11,
    },
    'pharma-precursor': {
        // Cryogenic — riding the cryo class is the ONLY way to get this from a
        // Culture Lab to a Cryo Tank without losing it all to boil-off.
        id: 'pharma-precursor',
        name: 'Pharma Precursor',
        tier: 2,
        phase: 'fluid',
        basePrice: 28,
        cryogenic: true,
    },
    // ── T3 Materials (P7B) ──────────────────────────────────────────────────
    superalloy: {
        // The high-tier metal stock. Forged in the Plasma Refinery from iron +
        // alumina. Feeds T3 mechanical chains (Machining Center → bearings) and
        // the Energy chain (plasma-cell).
        id: 'superalloy',
        name: 'Superalloy',
        tier: 3,
        phase: 'solid',
        basePrice: 60,
    },
    'ceramic-composite': {
        id: 'ceramic-composite',
        name: 'Ceramic Composite',
        tier: 3,
        phase: 'solid',
        basePrice: 45,
    },
    'precision-bearing': {
        // Top-tier mechanical part. Late-game contracts for these pay big.
        id: 'precision-bearing',
        name: 'Precision Bearing',
        tier: 3,
        phase: 'solid',
        basePrice: 110,
    },
    'semiconductor-wafer': {
        id: 'semiconductor-wafer',
        name: 'Semiconductor Wafer',
        tier: 3,
        phase: 'solid',
        basePrice: 95,
    },
    // ── T3 Energy (P7B) ─────────────────────────────────────────────────────
    'plasma-cell': {
        id: 'plasma-cell',
        name: 'Plasma Cell',
        tier: 3,
        phase: 'solid',
        basePrice: 140,
    },
    'lithium-ion-module': {
        id: 'lithium-ion-module',
        name: 'Lithium-Ion Module',
        tier: 3,
        phase: 'solid',
        basePrice: 160,
    },
    'fusion-precursor': {
        // Cryogenic — needs the Cryo class drone to move without flash-off.
        id: 'fusion-precursor',
        name: 'Fusion Precursor',
        tier: 3,
        phase: 'fluid',
        basePrice: 220,
        cryogenic: true,
    },
    // ── T3 Biotech (P7B) ────────────────────────────────────────────────────
    antibiotic: {
        id: 'antibiotic',
        name: 'Antibiotic',
        tier: 3,
        phase: 'fluid',
        basePrice: 85,
    },
    'synth-protein': {
        id: 'synth-protein',
        name: 'Synth Protein',
        tier: 3,
        phase: 'solid',
        basePrice: 70,
    },
    'organ-scaffold': {
        // Top-tier biotech good — endgame medical product in P7B.
        id: 'organ-scaffold',
        name: 'Organ Scaffold',
        tier: 3,
        phase: 'solid',
        basePrice: 180,
    },
    // ── T4 Materials + matrix infrastructure (P8+) ──────────────────────────
    // T4 is the late-game "Cohesion Matrix" tier — quantum substrates and
    // neural meshwork that the lore frames as the endgame networked-storage
    // tech. The catalog ships now; the actual networked-storage mechanic
    // lands in a follow-up phase.
    'mesh-composite': {
        // Matrix Core's keystone output — the binder that ties every other
        // T4 substrate together. Highest-volume T4 throughput.
        id: 'mesh-composite',
        name: 'Mesh Composite',
        tier: 4,
        phase: 'solid',
        basePrice: 320,
    },
    'exotic-alloy': {
        id: 'exotic-alloy',
        name: 'Exotic Alloy',
        tier: 4,
        phase: 'solid',
        basePrice: 280,
    },
    'quantum-circuit': {
        id: 'quantum-circuit',
        name: 'Quantum Circuit',
        tier: 4,
        phase: 'solid',
        basePrice: 480,
    },
    'quantum-crystal': {
        // Grown in the Crystal Lathe — feeds both circuits and disks.
        id: 'quantum-crystal',
        name: 'Quantum Crystal',
        tier: 4,
        phase: 'solid',
        basePrice: 360,
    },
    'matrix-disk': {
        // The literal disk that the future Matrix mechanic uses for capacity.
        // For now, a tradeable / buildable T4 good.
        id: 'matrix-disk',
        name: 'Matrix Disk',
        tier: 4,
        phase: 'solid',
        basePrice: 420,
    },
    // ── T4 Energy ────────────────────────────────────────────────────────────
    'photonic-cell': {
        id: 'photonic-cell',
        name: 'Photonic Cell',
        tier: 4,
        phase: 'solid',
        basePrice: 380,
    },
    'plasma-conduit': {
        id: 'plasma-conduit',
        name: 'Plasma Conduit',
        tier: 4,
        phase: 'solid',
        basePrice: 410,
    },
    'resonant-coil': {
        id: 'resonant-coil',
        name: 'Resonant Coil',
        tier: 4,
        phase: 'solid',
        basePrice: 520,
    },
    // ── T4 Biotech ───────────────────────────────────────────────────────────
    'neural-substrate': {
        id: 'neural-substrate',
        name: 'Neural Substrate',
        tier: 4,
        phase: 'solid',
        basePrice: 350,
    },
    'bio-neural-cortex': {
        // Top biotech good — a living computational substrate. Pricey because
        // it composes T3 biotech goods + T4 quantum circuits.
        id: 'bio-neural-cortex',
        name: 'Bio-Neural Cortex',
        tier: 4,
        phase: 'solid',
        basePrice: 700,
    },
    // ── T5 — Resonant Matrix endgame goods ───────────────────────────────────
    // The lore's three "ending goods" (Singularity Dust, Resonant Lattice, Aeon
    // Cell) anchor T5; the four supporting goods round out the catalog so each
    // of the three production tracks has both a keystone and a feeder. Every
    // T5 recipe consumes T4 inputs, which forces the player to keep the entire
    // cohesion-matrix chain running — there's no shortcut from T3 → T5.
    //
    // Prices are deliberately steep (1.4–4× the most expensive T4 good) so a
    // single completed T5 contract is a meaningful market event.
    'singularity-dust': {
        // Keystone exotic-matter substrate — fed into every other T5 recipe.
        id: 'singularity-dust',
        name: 'Singularity Dust',
        tier: 5,
        phase: 'solid',
        basePrice: 1100,
    },
    'resonant-lattice': {
        // The endgame structural composite — a woven matrix of mesh-composite
        // bound by singularity-dust. Resonates: future events / autofab reads it.
        id: 'resonant-lattice',
        name: 'Resonant Lattice',
        tier: 5,
        phase: 'solid',
        basePrice: 1500,
    },
    'void-glass': {
        // Exotic transparent substrate from the Void Fabricator. Feeds aeon cells
        // and zero-point conduits.
        id: 'void-glass',
        name: 'Void Glass',
        tier: 5,
        phase: 'solid',
        basePrice: 950,
    },
    'aeon-cell': {
        // Endgame battery — combines the photonic and resonant tracks. Highest-
        // density energy unit in the game.
        id: 'aeon-cell',
        name: 'Aeon Cell',
        tier: 5,
        phase: 'solid',
        basePrice: 1800,
    },
    'zero-point-conduit': {
        // T5 power-distribution upgrade. Pairs with aeon cells in the lore's
        // resonant-grid pitch; for now it's a tradeable / contractable good.
        id: 'zero-point-conduit',
        name: 'Zero-Point Conduit',
        tier: 5,
        phase: 'solid',
        basePrice: 1300,
    },
    'transcendent-cortex': {
        // Top biotech good — sublimates the bio-neural-cortex into a singular
        // network-grade computational organ. Most expensive item in the catalog.
        id: 'transcendent-cortex',
        name: 'Transcendent Cortex',
        tier: 5,
        phase: 'solid',
        basePrice: 2400,
    },
    'gene-archive': {
        // T5 biotech datastore — a sequenced living archive. Cross-track because
        // its recipe needs a matrix-disk to encode the genome.
        id: 'gene-archive',
        name: 'Gene Archive',
        tier: 5,
        phase: 'solid',
        basePrice: 1700,
    },
    // ── P15 — Cross-track endgame goods (lore §4) ────────────────────────────
    // The "trinity" goods. Each requires keystones from two or three tracks
    // simultaneously; producing all three is the gate for Galactic Laureate.
    // Pricing reflects "vanity sells" — these exist as proof of mastery, not
    // as recipe inputs for anything else (yet).
    chronosteel: {
        // Forged in the Singularity Forge from the keystones of all three tracks
        // (resonant-lattice + aeon-cell + transcendent-cortex) bound by
        // singularity-dust. Highest-priced material in the catalog.
        id: 'chronosteel',
        name: 'Chronosteel',
        tier: 5,
        phase: 'solid',
        basePrice: 5000,
    },
    eternium: {
        // Cast in the Aeon Reactor — a stabilised energy-lattice composite that
        // weaves quantum circuitry into a power-harvesting structure.
        id: 'eternium',
        name: 'Eternium',
        tier: 5,
        phase: 'solid',
        basePrice: 4500,
    },
    pyriton: {
        // Vat-grown in the Transcendent Vat — a self-replicating biotech-energy
        // amalgam encoded onto a void-glass substrate.
        id: 'pyriton',
        name: 'Pyriton',
        tier: 5,
        phase: 'solid',
        basePrice: 4200,
    },
};
export const ALL_RESOURCE_IDS = Object.keys(RESOURCES);
export function getResource(id) {
    const r = RESOURCES[id];
    if (!r)
        throw new Error(`Unknown resource: ${id}`);
    return r;
}
/** Phase lookup helper used by storage filters and drone class compatibility. */
export function getResourcePhase(id) {
    return getResource(id).phase;
}
/** True iff this resource must ride the cryo drone class. */
export function isResourceCryogenic(id) {
    return getResource(id).cryogenic === true;
}
//# sourceMappingURL=resources.js.map